import { Container, Grid, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';

import { showNotification } from '@mantine/notifications';
import React, { ReactNode, useEffect } from 'react';
import { X } from 'tabler-icons-react';
import { UserManagementFilterMode } from '../../enums/misc.enum';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { ErrorResponse } from '../../type/error-response.types';
import {
  SelectData,
  UserManagementFilterData,
  UserManagementFilterForm,
} from '../../type/filter.types';
import { BasicPos } from '../../type/select.types';

const ResponsiveCol = ({ children }: { children: ReactNode }) => {
  return (
    <Grid.Col xs={12} sm={6}>
      {children}
    </Grid.Col>
  );
};

interface Props {
  filterValues: UseFormReturnType<UserManagementFilterForm>;
  setIsLoadPOS: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadPOS: boolean;
  filterSelectData: UseFormReturnType<UserManagementFilterData>;
  mode?: UserManagementFilterMode;
}

function BranchesPaymentLinkFilter(props: Props) {
  const { filterValues, setIsLoadPOS, isLoadPOS, filterSelectData } = props;
  const axiosPrivate = useAxiosPrivate();

  const selectLoadingData = useForm({
    initialValues: {
      isPartnersLoading: false,
      isResellersLoading: false,
      isMerchantsLoading: false,
      isBranchesLoading: false,
      isPosLoading: false,
    },
  });

  const findValueInData = (data: SelectData[], value: string): boolean => {
    for (let i = 0; i < data?.length; i++) {
      if (data[i].value === value) {
        return true;
      }
    }
    return false;
  };

  const noData = 'No data';
  const loadingData = 'Loading...';

  useEffect(() => {
    let ignore = false;

    const loadPos = async () => {
      selectLoadingData.setFieldValue('isPosLoading', true);

      try {
        const response = await axiosPrivate.get('/web/select/pos');

        const pos = response.data?.items?.map((merchant: BasicPos) => {
          return {
            label: merchant.posName,
            value: merchant.posId.toString(),
          };
        });

        if (!ignore) {
          const found = findValueInData(pos, filterValues.values.pos || '');

          if (!found) {
            filterValues.setFieldValue('pos', '');
          }
          filterSelectData.setFieldValue('posData', pos);
        }
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-pos-data',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        selectLoadingData.setFieldValue('isPosLoading', false);
      }
    };

    if (isLoadPOS) {
      loadPos();
    } else {
      setIsLoadPOS(false);
    }

    return () => {
      ignore = true;
    };
  }, []);

  return (
    <>
      <Container fluid p={0} m={0}>
        <Grid>
          <ResponsiveCol>
            <Select
              label='Select POS'
              data={
                selectLoadingData.values.isPosLoading
                  ? [loadingData]
                  : !filterSelectData.values.posData //if posData is undefined
                  ? [noData]
                  : filterSelectData.values.posData?.length === 0
                  ? [noData]
                  : filterSelectData.values.posData
              }
              value={
                selectLoadingData.values.isPosLoading
                  ? loadingData
                  : filterSelectData.values.posData?.length === 0
                  ? noData
                  : filterValues.values.pos
              }
              disabled={
                selectLoadingData.values.isPosLoading ||
                filterSelectData.values.posData?.length === 0
              }
              onChange={(value: string) =>
                filterValues.setFieldValue('pos', value)
              }
              clearable={
                !(
                  selectLoadingData.values.isPosLoading ||
                  filterSelectData.values.posData?.length === 0
                )
              }
              searchable
            />
          </ResponsiveCol>
        </Grid>
      </Container>
    </>
  );
}

export default BranchesPaymentLinkFilter;
